import { EventEmitter, Injectable, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { IEventoGenerico } from 'modelos/src';
import { Howl } from 'howler';
import { IdleService } from './idle.service';
import { Lista2Service } from './lista2.service';
import { HelperService } from './helper.service';

/**
 * Cadencia del polling del listado vivo.
 *
 * Este listado NO usa el WS. Estaba suscripto a la entidad `eventogenerico`
 * usando el mensaje solo como trigger de un GET, con debounce de 1500ms — y el
 * room de esa entidad mueve decenas de mensajes por segundo (lo documenta
 * gestion-api-websocket-io en rooms.ts). Un debounce nunca emite si los
 * mensajes llegan más seguido que su ventana: el trigger no disparaba nunca y
 * el listado quedaba refrescando a la cadencia RELAJADA de 90s que se había
 * elegido justamente porque se suponía que el WS era el canal principal. O sea
 * que la suscripción no solo no servía: empeoraba el refresco.
 *
 * Un polling fijo de 10s es más simple y más rápido que lo que había.
 */
const INTERVALO_POLLING_SEGUNDOS = 10;

@Injectable({
  providedIn: 'root',
})
export class ListadoEventosGenericosVivoService {
  private lista2Service = inject(Lista2Service);
  private idleService = inject(IdleService);
  private helperService = inject(HelperService);

  /** Ids del listado anterior: alcanza para detectar eventos nuevos */
  private idsAnteriores = new Set<string>();
  public eventos: IEventoGenerico[] = [];
  public totalCount = 0;

  private sonidos = {
    Fondo: new Howl({
      src: ['/assets/sonidos/empty_loop_for_js_performance.ogg'],
      volume: 0.5,
      autoplay: false,
      loop: true,
    }),
    Sirena: new Howl({
      src: ['/assets/sonidos/sirena1.mp3'],
      volume: 1,
      autoplay: false,
      loop: false,
    }),
    Campana: new Howl({
      src: ['/assets/sonidos/campana.mp3'],
      volume: 1,
      autoplay: false,
      loop: false,
    }),
  };

  private eventos$?: Subscription;
  private idle$?: Subscription;
  public eventosChange = new EventEmitter<IEventoGenerico[]>();

  private async listar(): Promise<void> {
    this.eventos$?.unsubscribe();
    this.eventos$ = this.lista2Service
      .eventosGenericosVivo({ includeChildren: true }, INTERVALO_POLLING_SEGUNDOS)
      .subscribe((data) => {
        this.totalCount = data.length;
        // Sin clonar: los datos vienen frescos del http y nadie los muta.
        // El clon anterior rompía la identidad de cada evento y forzaba a
        // re-renderizar todas las tablas del listado en cada refresh.
        const eventoNuevo = data.find((e) => !this.idsAnteriores.has(e._id));
        this.idsAnteriores = new Set(data.map((e) => e._id));
        this.eventos = data;
        this.eventosChange.emit(this.eventos);
        this.reproducirSonido(eventoNuevo);
      });
  }

  public async forceRefresh(): Promise<void> {
    await this.listar();
  }

  private reproducirSonido(eventoNuevo?: IEventoGenerico) {
    if (this.helperService.sonidoMuteado) return;
    if (eventoNuevo) {
      const sonido: any = eventoNuevo.valores?.sonido;
      switch (sonido) {
        case 'Silencio':
          break;
        case 'Sirena':
          this.sonidos.Sirena.play();
          break;
        case 'Campana':
          this.sonidos.Campana.play();
          break;
        default:
          this.sonidos.Campana.play();
          break;
      }
    }
  }

  private InitBackSound() {
    if (this.helperService.sonidoMuteado) return;
    this.sonidos.Fondo.play();
  }

  private stopBackSound() {
    this.sonidos.Fondo.stop();
  }

  public async iniciarServicio() {
    // Una sola vez por sesión: NavigationComponent llama iniciarServicio() en
    // cada creación (ej: logout → login) y los handlers se acumulaban.
    if (!this.idle$) {
      this.idle$ = new Subscription();
      this.idle$.add(
        this.idleService.idle$.subscribe(() => this.InitBackSound()),
      );
      this.idle$.add(
        this.idleService.wake$.subscribe(() => this.stopBackSound()),
      );
    }
    await this.listar();
  }

  public async reiniciar() {
    this.eventos$?.unsubscribe();
    this.eventos = [];
    this.idsAnteriores = new Set();
    this.totalCount = 0;
    await this.listar();
  }
}
